import { PlacesBlock } from './shared/PlacesBlock';
import { ShowroomQrCodes } from './shared/ShowroomQrCodes';
import Reveal from './shared/Reveal';
import { useContactInfo } from '../hooks/useContactInfo';
import { useCars } from '../context/CarsContext';
import { config } from '../config/runtimeConfig';
import './page-layout.css';
import './showrooms.css';
import { useTranslation } from '../i18n/I18nContext';

/**
 * «Showroom» — салоны, карта и QR-коды (#1337, план #1330).
 *
 * Отдельный раздел меню, а не хвост «Контактов»: так это описал Антон на синке
 * #237 («потом showroom блок, в меню называется showroom, и у тебя раздел с
 * этим QR-кодом»). Приехать в салон и написать в мессенджер — разные намерения,
 * и раздел «связаться» их смешивал.
 *
 * ⚠️ Адрес `/showrooms`, во множественном числе. `/showroom` занят
 * Autodis-флоу со свайп-каталогом (`ShowroomPage`, за флагом
 * `ENABLE_SHOWROOM_CATALOG`) — там своя страница и свой дилер, трогать её
 * ради витрины нельзя.
 *
 * Страница только standalone: в App.jsx её маршрут, в бандл виджета не входит.
 */
const ShowroomsPage = () => {
  const { t } = useTranslation();
  const contactInfo = useContactInfo();
  const { getAllCars } = useCars();

  // Метка на карте и карточка салона связаны: наведение на карточку зажигает
  // метку, клик по метке выделяет карточку. Иначе два адреса и две точки
  // остаются двумя несвязанными списками.

  // QR ведут на саму витрину, поэтому origin берём из окна. Машина — первая из
  // РЕАЛЬНОГО инвентаря: хардкоженный id увёл бы код в никуда на другом
  // источнике.
  const qrOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const qrCar = getAllCars()[0] || null;
  const qrCarUrl = qrCar
    ? `${qrOrigin}/#/cars/${qrCar.id}/landing`
    : `${qrOrigin}/#/cars`;
  // Код на двери — в каталог шоурума (#1441): одна сетка карточек, без фильтров
  // и подвала, свайп между лендингами. Без потока шоурума — обычный каталог.
  const qrCatalogUrl = config.ENABLE_SHOWROOM_CATALOG
    ? `${qrOrigin}/#/showroom`
    : `${qrOrigin}/#/cars`;

  return (
    <div className="page">
      <div className="page__container">
        <header className="showrooms__hero">
          <h1 className="showrooms__title">
            {t('showcase.showrooms.title', 'Our showrooms')}
          </h1>
          <p className="showrooms__lead">
            {t(
              'showcase.showrooms.lead',
              'Two locations, one stock — a car listed in Luxembourg can be delivered to Cyprus'
            )}
          </p>
        </header>

        {/* Оба сценария с кодом — ОДНИМ бенто (#1417): сверху салон с кодом
            на двери и плитка «Closed for the day?», снизу плитка «After hours»
            и машина с кодом на лобовом. Снимки идут по диагонали, плитки
            текста — тоже: два сценария читаются парой, а не двумя разделами
            подряд. У снимков и карты скруглены все углы, внутренние тоже.

            В разметке текст стоит ПЕРЕД своим снимком: в стопке на узком
            экране он объясняет, что искать на кадре, и читать его надо до. */}
        <Reveal as="section" className="showrooms__section showrooms__bento">
          <div className="showrooms__bento-copy showrooms__bento-copy--door">
            <h2 className="showrooms__section-title">
              {t('showcase.showrooms.doorTitle', 'Closed for the day?')}
            </h2>
            <p className="showrooms__section-lead">
              {t(
                'showcase.showrooms.doorLead',
                'The code on the door opens the whole stock, assistant included'
              )}
            </p>
          </div>
          <ShowroomQrCodes
            carUrl={qrCatalogUrl}
            photo="/assets/showroom.webp"
            corner="bottom-start"
            arrowShape="sweep"
            className="showrooms__bento-shot showrooms__bento-shot--door"
          />
          <div className="showrooms__bento-copy showrooms__bento-copy--glass">
            <h2 className="showrooms__section-title">
              {t('showcase.showrooms.afterHours', 'After hours')}
            </h2>
            <p className="showrooms__section-lead">
              {t(
                'showcase.showrooms.afterHoursLead',
                'Outside opening hours the showroom keeps selling: a code on the windscreen opens the same site, assistant included'
              )}
            </p>
          </div>
          <ShowroomQrCodes
            carUrl={qrCarUrl}
            className="showrooms__bento-shot showrooms__bento-shot--glass"
          />
        </Reveal>

      </div>

      {/* Салоны — тот же блок, что на главной (#1335), один в один: белая
          полоса во всю ширину со скруглением, заголовок, подпись и адреса
          без часов, справа карта. Отдельным разделом после бенто. */}
      {contactInfo.locations?.length > 0 && (
        <Reveal as="section" className="showrooms__places-band">
          <div className="page__container page__container--narrow">
            <PlacesBlock
              locations={contactInfo.locations}
              details={false}
              title={t('showcase.home.placesTitle', 'Visit our showrooms')}
              lead={t(
                'showcase.home.placesLead',
                'Come in for a test drive, or ask the assistant anything before you do'
              )}
            />
          </div>
        </Reveal>
      )}
    </div>
  );
};

export default ShowroomsPage;
